import { createWorker } from 'tesseract.js';
import Papa from 'papaparse';
import { PDFDocument } from 'pdf-lib';
import { z } from 'zod';

// Transaction schema for validation
const TransactionSchema = z.object({
  date: z.string().refine(val => !isNaN(Date.parse(val)), {
    message: "Invalid date format"
  }),
  amount: z.number(),
  description: z.string().min(1),
  category: z.string().optional(),
  type: z.enum(['income', 'expense']).optional()
});

export const processCSV = async (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      complete: (results) => {
        try {
          const validatedData = validateTransactionData(results.data);
          resolve(validatedData);
        } catch (error) {
          reject(new Error('Invalid CSV data format'));
        }
      },
      error: (error) => reject(error),
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.toLowerCase().trim(),
      transform: (value) => value.trim()
    });
  });
};

export const processPDF = async (file: File): Promise<any> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const text = await extractTextFromPDF(pdfDoc);
    const transactions = parseTransactionData(text);
    return validateTransactionData(transactions);
  } catch (error) {
    console.error('Error processing PDF:', error);
    throw new Error('Failed to process PDF file');
  }
};

const extractTextFromPDF = async (pdfDoc: PDFDocument): Promise<string> => {
  // Note: pdf-lib doesn't support direct text extraction
  // We'll need to use a different approach or library for text extraction
  // For now, we'll return a placeholder message
  console.warn('PDF text extraction is not supported with pdf-lib. Consider using pdf.js or other PDF parsing libraries.');
  return 'PDF text extraction not supported';
};

const parseTransactionData = (text: string): any[] => {
  // Enhanced pattern matching for various date formats
  const datePatterns = [
    /\d{1,2}[-/]\d{1,2}[-/]\d{2,4}/g,                    // MM/DD/YYYY or DD/MM/YYYY
    /\d{4}[-/]\d{1,2}[-/]\d{1,2}/g,                      // YYYY/MM/DD
    /(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2},?\s+\d{4}/gi  // Month DD, YYYY
  ];

  // Enhanced amount pattern matching
  const amountPattern = /(?:[\$£€]\s*)?[-]?\d+(?:[,.]\d{2})?/g;
  
  // Description pattern - looks for text between date and amount
  const descriptionPattern = /[a-zA-Z0-9\s&.,'-]+/g;

  let transactions: any[] = [];
  let currentPosition = 0;

  while (currentPosition < text.length) {
    let dateMatch: RegExpExecArray | null = null;
    let earliestPosition = text.length;
    let matchedDate = '';

    // Find the earliest date match from any pattern
    for (const pattern of datePatterns) {
      pattern.lastIndex = currentPosition;
      const match = pattern.exec(text);
      if (match && match.index < earliestPosition) {
        earliestPosition = match.index;
        dateMatch = match;
        matchedDate = match[0];
      }
    }

    if (!dateMatch) break;

    // Look for amount after the date
    amountPattern.lastIndex = dateMatch.index + dateMatch[0].length;
    const amountMatch = amountPattern.exec(text);
    
    if (amountMatch) {
      // Extract description between date and amount
      const descriptionText = text.slice(
        dateMatch.index + dateMatch[0].length,
        amountMatch.index
      );
      
      const descriptionMatches = descriptionText.match(descriptionPattern);
      const description = descriptionMatches 
        ? descriptionMatches.join(' ').trim()
        : 'Unknown';

      // Parse amount and clean it up
      let amount = amountMatch[0].replace(/[^\d.-]/g, '');
      amount = parseFloat(amount);

      if (!isNaN(amount)) {
        transactions.push({
          date: standardizeDate(matchedDate),
          amount: amount,
          description: description,
          type: amount >= 0 ? 'income' : 'expense'
        });
      }
    }

    currentPosition = dateMatch.index + dateMatch[0].length;
  }

  return transactions;
};

const standardizeDate = (dateStr: string): string => {
  // Handle various date formats and convert to YYYY-MM-DD
  const cleanDate = dateStr.replace(/[,]/g, '');
  
  // Try parsing with Date.parse first
  let date = new Date(cleanDate);
  
  if (isNaN(date.getTime())) {
    // Handle specific formats manually
    const parts = cleanDate.split(/[-/\s]+/);
    
    if (parts.length === 3) {
      // Handle MM/DD/YYYY or DD/MM/YYYY
      if (parts[2].length === 2) {
        parts[2] = '20' + parts[2]; // Assume 20xx for 2-digit years
      }
      
      // Try both MM/DD/YYYY and DD/MM/YYYY
      date = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
      if (isNaN(date.getTime())) {
        date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
    }
  }
  
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date format: ${dateStr}`);
  }
  
  return date.toISOString().split('T')[0];
};

export const extractTextFromImage = async (file: File): Promise<string> => {
  try {
    const worker = await createWorker('eng');
    const { data: { text } } = await worker.recognize(file);
    await worker.terminate();
    return text;
  } catch (error) {
    console.error('Error processing image:', error);
    throw new Error('Failed to extract text from image');
  }
};

export const validateTransactionData = (data: any[]): any[] => {
  return data.filter(record => {
    try {
      // Parse the record through our schema
      const validatedRecord = TransactionSchema.parse({
        date: record.date,
        amount: typeof record.amount === 'string' ? parseFloat(record.amount) : record.amount,
        description: record.description || 'Unknown Transaction',
        category: record.category,
        type: record.type || (parseFloat(record.amount) >= 0 ? 'income' : 'expense')
      });

      // Additional custom validations
      const date = new Date(validatedRecord.date);
      if (date > new Date() || date.getFullYear() < 1900) {
        return false;
      }

      if (Math.abs(validatedRecord.amount) > 1000000000) { // Unreasonable amount check
        return false;
      }

      return true;
    } catch (error) {
      console.warn('Invalid record:', record, error);
      return false;
    }
  });
};

// Helper function to detect transaction patterns
export const detectTransactionPatterns = (transactions: any[]): any => {
  const patterns = {
    regularPayments: new Map<string, number>(),
    unusualAmounts: [],
    categories: new Map<string, number>(),
    frequentMerchants: new Map<string, number>()
  };

  transactions.forEach(transaction => {
    // Track regular payment amounts
    const amount = Math.abs(transaction.amount);
    patterns.regularPayments.set(
      amount,
      (patterns.regularPayments.get(amount) || 0) + 1
    );

    // Track categories
    if (transaction.category) {
      patterns.categories.set(
        transaction.category,
        (patterns.categories.get(transaction.category) || 0) + 1
      );
    }

    // Track merchants
    patterns.frequentMerchants.set(
      transaction.description,
      (patterns.frequentMerchants.get(transaction.description) || 0) + 1
    );

    // Detect unusual amounts
    if (amount % 1 !== 0 && amount % 0.99 !== 0) {
      patterns.unusualAmounts.push(transaction);
    }
  });

  return patterns;
};